import React, { useEffect, useMemo, useState } from "react";
import { TextField, Tooltip, Typography } from "@material-ui/core";
import { Alert, Autocomplete, Skeleton } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis
} from "recharts";

import api from "../../../../services/api";
import {
  ChartTooltipBox, DataTable, EmptyState, OsLink, Panel, STATUS_COLORS, StatCard, axisProps, blockColor, chartInk,
  dateLabel, hoursLabel, numberLabel, seriesColor, toLocalDate
} from "./shared";

const DAY = 86400000;

const useStyles = makeStyles(theme => ({
  grid: { display: "grid", gap: theme.spacing(2), gridTemplateColumns: "minmax(0, 1fr)" },
  selector: { maxWidth: 560 },
  cards: { display: "grid", gap: theme.spacing(1.5), gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" },
  twoColumns: {
    display: "grid", gap: theme.spacing(2), gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" }
  },
  progressTrack: { height: 12, borderRadius: 6, background: theme.palette.action.hover, overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: 6 },
  progressMeta: { display: "flex", justifyContent: "space-between", fontSize: 12, color: theme.palette.text.secondary, marginTop: 6 },
  hoursRow: { display: "grid", gridTemplateColumns: "90px minmax(0, 1fr) 80px", alignItems: "center", gap: theme.spacing(1), margin: theme.spacing(1, 0) },
  hoursLabel: { fontSize: 12, color: theme.palette.text.secondary },
  hoursValue: { fontSize: 13, fontWeight: 600, textAlign: "right", fontVariantNumeric: "tabular-nums" },
  hoursTrack: { height: 14, borderRadius: 4, background: theme.palette.action.hover },
  hoursFill: { height: "100%", borderRadius: 4, minWidth: 2 },
  gantt: { overflowX: "auto" },
  ganttInner: { minWidth: 680 },
  ganttRow: {
    display: "grid", gridTemplateColumns: "220px minmax(0, 1fr)", alignItems: "center", gap: theme.spacing(1.5),
    minHeight: 40, "& + &": { borderTop: `1px solid ${theme.palette.divider}` }
  },
  ganttLabel: { fontSize: 12, lineHeight: 1.3, minWidth: 0, padding: theme.spacing(0.5, 0) },
  ganttStatus: { fontSize: 11, color: theme.palette.text.secondary },
  ganttTrack: { position: "relative", alignSelf: "stretch", minHeight: 40 },
  ganttGridLine: { position: "absolute", top: 0, bottom: 0, width: 1 },
  ganttToday: { position: "absolute", top: 0, bottom: 0 },
  ganttPlanned: { position: "absolute", top: "calc(50% - 9px)", height: 7, borderRadius: 4, minWidth: 6 },
  ganttActual: { position: "absolute", top: "calc(50% + 1px)", height: 9, borderRadius: 4, minWidth: 6 },
  ganttMarker: {
    position: "absolute", top: "calc(50% + 1px)", width: 9, height: 9, marginLeft: -4.5,
    transform: "rotate(45deg)", borderRadius: 1
  },
  ganttAxis: { position: "relative", height: 22, fontSize: 10, color: theme.palette.text.secondary },
  ganttTick: { position: "absolute", bottom: 4, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  ganttTodayLabel: { position: "absolute", top: 0, transform: "translateX(-50%)", fontSize: 10, fontWeight: 600, color: theme.palette.text.primary },
  tooltipTitle: { fontWeight: 600, marginBottom: 2 },
  legend: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.5, 2), fontSize: 12, color: theme.palette.text.secondary, marginBottom: theme.spacing(1) },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 6 },
  late: { fontWeight: 600, color: theme.palette.type === "dark" ? theme.palette.error.light : theme.palette.error.dark }
}));

const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const dayDiff = (from, to) => Math.round((to - from) / DAY);
const shortDate = date => date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
const percent = (value, start, span) => ((value - start) / span) * 100;
const daysText = days => `${days} ${Math.abs(days) === 1 ? "dia" : "dias"}`;

// Escala adaptada à duração: dias (até 3 semanas), semanas (até 4 meses) ou meses.
const buildScale = (start, end) => {
  const days = dayDiff(start, end);
  const ticks = [];
  if (days <= 21) {
    const step = days <= 14 ? 1 : 2;
    for (let date = start, index = 0; date < end; date = addDays(date, 1), index += 1) {
      ticks.push({ date, label: index % step === 0 ? shortDate(date) : "", centered: true });
    }
  } else if (days <= 120) {
    const firstMonday = addDays(start, (8 - start.getDay()) % 7);
    for (let date = firstMonday; date < end; date = addDays(date, 7)) {
      ticks.push({ date, label: shortDate(date) });
    }
  } else {
    const step = days > 540 ? 3 : 1;
    for (let date = new Date(start.getFullYear(), start.getMonth() + 1, 1); date < end; date = new Date(date.getFullYear(), date.getMonth() + step, 1)) {
      ticks.push({ date, label: date.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }) });
    }
  }
  return ticks;
};

const StageTooltip = ({ stage, planned, actual, conclusionOnly }) => {
  const classes = useStyles();
  const plannedText = planned
    ? `${dateLabel(stage.previsao_inicio || stage.previsao_termino)} a ${dateLabel(stage.previsao_termino || stage.previsao_inicio)} (${daysText(dayDiff(planned.start, planned.end) + 1)})`
    : "sem previsão";
  let actualText = "não iniciada";
  if (actual) {
    actualText = `${dateLabel(stage.inicio_real)} a ${stage.concluida_em ? dateLabel(stage.concluida_em) : "hoje (em andamento)"} (${daysText(dayDiff(actual.start, actual.end) + 1)})`;
  } else if (conclusionOnly) {
    actualText = `concluída em ${dateLabel(stage.concluida_em)}, sem início registrado`;
  }
  let deviation = null;
  if (stage.concluida_em && stage.previsao_termino) {
    const days = dayDiff(toLocalDate(stage.previsao_termino), toLocalDate(stage.concluida_em));
    deviation = days > 0 ? `concluída ${daysText(days)} após o previsto` : days < 0 ? `concluída ${daysText(-days)} antes do previsto` : "concluída no dia previsto";
  } else if (stage.atrasada) {
    deviation = `atrasada ${daysText(stage.dias_atraso)}`;
  }
  return (
    <div>
      <div className={classes.tooltipTitle}>{stage.rotulo} · {stage.status}</div>
      <div>Previsto: {plannedText}</div>
      <div>Real: {actualText}</div>
      {deviation && <div>Desvio: {deviation}</div>}
    </div>
  );
};

const Gantt = ({ stages, generatedAt }) => {
  const classes = useStyles();
  const theme = useTheme();
  const ink = chartInk(theme);
  const today = toLocalDate(generatedAt);
  const todayFill = theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(11,11,11,0.06)";

  if (!stages.length) return <EmptyState>Projeto sem etapas.</EmptyState>;

  const rows = stages.map(stage => {
    const plannedStart = toLocalDate(stage.previsao_inicio || stage.previsao_termino);
    const plannedEnd = toLocalDate(stage.previsao_termino || stage.previsao_inicio);
    const planned = plannedStart && plannedEnd
      ? { start: new Date(Math.min(plannedStart, plannedEnd)), end: new Date(Math.max(plannedStart, plannedEnd)) }
      : null;
    const actualStart = toLocalDate(stage.inicio_real);
    const actualEnd = toLocalDate(stage.concluida_em) || today;
    const actual = actualStart ? { start: actualStart, end: new Date(Math.max(actualStart, actualEnd)) } : null;
    const conclusionOnly = !actualStart && stage.concluida_em ? toLocalDate(stage.concluida_em) : null;
    return { stage, planned, actual, conclusionOnly };
  });

  // Cada dia é uma célula: a barra vai do início do primeiro dia ao fim do último.
  const dates = rows.flatMap(row => [row.planned?.start, row.planned?.end, row.actual?.start, row.actual?.end, row.conclusionOnly])
    .filter(Boolean).concat(today);
  const start = addDays(new Date(Math.min(...dates)), -1);
  const end = addDays(new Date(Math.max(...dates)), 2);
  const span = end - start;
  const ticks = buildScale(start, end);
  const left = date => `${percent(date, start, span)}%`;
  const todayPosition = percent(today.getTime() + DAY / 2, start, span);
  const labelPosition = tick => percent(tick.date.getTime() + (tick.centered ? DAY / 2 : 0), start, span);
  // Rótulos que encostariam no marcador "Hoje" ou na borda direita ficam só com a linha de grade.
  const visibleLabels = ticks.filter(tick => tick.label && labelPosition(tick) < 96
    && Math.abs(labelPosition(tick) + (tick.centered ? 0 : 2) - todayPosition) > 4);
  const width = (from, to) => `${((dayDiff(from, to) + 1) * DAY / span) * 100}%`;

  return (
    <div className={classes.gantt}>
      <div className={classes.ganttInner}>
        <div className={classes.legend}>
          <span className={classes.legendItem}><span style={{ width: 18, height: 7, borderRadius: 4, background: ink.axis }} />Previsto</span>
          <span className={classes.legendItem}><span style={{ width: 18, height: 9, borderRadius: 4, background: seriesColor(theme, 0) }} />Real, na cor do bloco</span>
          <span className={classes.legendItem}>
            <span style={{ width: 9, height: 9, transform: "rotate(45deg)", background: seriesColor(theme, 0), borderRadius: 1 }} />Concluída sem início registrado
          </span>
          <span className={classes.legendItem}><span style={{ width: 14, height: 14, background: todayFill, border: `1px solid ${ink.axis}` }} />Hoje</span>
        </div>
        <div className={classes.ganttRow} style={{ minHeight: 24 }}>
          <div />
          <div className={classes.ganttAxis} aria-hidden="true">
            <span className={classes.ganttTodayLabel} style={{ left: `${todayPosition}%` }}>Hoje</span>
            {visibleLabels.map(tick => (
              <span key={tick.date.getTime()} className={classes.ganttTick} style={tick.centered
                ? { left: `${labelPosition(tick)}%`, transform: "translateX(-50%)" }
                : { left: left(tick.date), paddingLeft: 3 }}>
                {tick.label}
              </span>
            ))}
          </div>
        </div>
        {rows.map(({ stage, planned, actual, conclusionOnly }) => {
          const color = blockColor(theme, stage.bloco);
          return (
            <div key={stage.os_id} className={classes.ganttRow}>
              <div className={classes.ganttLabel}>
                <div><OsLink id={stage.os_id} /> {stage.rotulo}</div>
                <div className={classes.ganttStatus}>
                  {stage.status}{stage.tecnico ? ` · ${stage.tecnico}` : ""}
                  {stage.atrasada && <span className={classes.late}> · atrasada {stage.dias_atraso} d</span>}
                </div>
              </div>
              <Tooltip arrow placement="top"
                title={<StageTooltip stage={stage} planned={planned} actual={actual} conclusionOnly={conclusionOnly} />}>
                <div className={classes.ganttTrack}>
                  <div className={classes.ganttToday} style={{ left: left(today), width: width(today, today), background: todayFill }} />
                  {ticks.map(tick => (
                    <div key={tick.date.getTime()} className={classes.ganttGridLine} style={{ left: left(tick.date), background: ink.grid }} />
                  ))}
                  {planned && (
                    <div className={classes.ganttPlanned} style={{ left: left(planned.start), width: width(planned.start, planned.end), background: ink.axis }} />
                  )}
                  {actual && (
                    <div className={classes.ganttActual} style={{ left: left(actual.start), width: width(actual.start, actual.end), background: color }} />
                  )}
                  {conclusionOnly && (
                    <div className={classes.ganttMarker} style={{ left: `${percent(conclusionOnly.getTime() + DAY / 2, start, span)}%`, background: color }} />
                  )}
                </div>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HoursComparison = ({ hours }) => {
  const classes = useStyles();
  const theme = useTheme();
  if (!hours.minutos_gastos && !hours.minutos_estimados) {
    return (
      <EmptyState>
        Nenhuma hora registrada nem estimada nas etapas e customizações deste projeto.
        As horas vêm do tempo gasto da OS ou dos registros de tempo em execução.
      </EmptyState>
    );
  }
  const max = Math.max(hours.minutos_gastos, hours.minutos_estimados, 1);
  const rows = [
    { label: "Gastas", value: hours.minutos_gastos, color: seriesColor(theme, 0) },
    ...(hours.minutos_estimados ? [{ label: "Estimadas", value: hours.minutos_estimados, color: seriesColor(theme, 1) }] : [])
  ];
  return (
    <div>
      {rows.map(row => (
        <div key={row.label} className={classes.hoursRow}>
          <span className={classes.hoursLabel}>{row.label}</span>
          <div className={classes.hoursTrack}>
            <div className={classes.hoursFill} style={{ width: `${(row.value / max) * 100}%`, background: row.color }} />
          </div>
          <span className={classes.hoursValue}>{hoursLabel(row.value)}</span>
        </div>
      ))}
      <Typography variant="caption" color="textSecondary">
        {hours.minutos_estimados
          ? `${numberLabel((hours.minutos_gastos / hours.minutos_estimados) * 100, 0)}% do estimado. Estimativa ${hours.origem_estimado === "pai" ? "da OS pai" : "somada das filhas"}.`
          : "Sem estimativa cadastrada na OS pai nem nas filhas."}
      </Typography>
    </div>
  );
};

const HoursByStage = ({ rows }) => {
  const theme = useTheme();
  const ink = chartInk(theme);
  if (!rows.some(row => row.minutos > 0)) return <EmptyState>Nenhuma hora registrada nas etapas deste projeto.</EmptyState>;
  const data = rows.map(row => ({ ...row, horas: Math.round((row.minutos / 60) * 10) / 10 }));
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, data.length * 30 + 40)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 8 }} barCategoryGap={6}>
        <CartesianGrid horizontal={false} stroke={ink.grid} />
        <XAxis type="number" unit=" h" {...axisProps(theme)} />
        <YAxis type="category" dataKey="label" width={170} {...axisProps(theme)} />
        <ChartTooltip cursor={{ fill: theme.palette.action.hover }} content={({ active, payload }) => (active && payload?.length ? (
          <ChartTooltipBox title={payload[0].payload.label} rows={[
            { label: "Gastas", value: hoursLabel(payload[0].payload.minutos), color: seriesColor(theme, 0) },
            { label: "Estimadas", value: hoursLabel(payload[0].payload.minutos_estimados) }
          ]} />
        ) : null)} />
        <Bar dataKey="horas" name="Horas gastas" fill={seriesColor(theme, 0)} radius={[0, 4, 4, 0]} maxBarSize={20} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const TicketsByWeek = ({ tickets }) => {
  const theme = useTheme();
  const ink = chartInk(theme);
  const weeks = tickets.semanas;
  if (!weeks.length) return <EmptyState>Sem cliente ou período para consultar chamados.</EmptyState>;
  if (!weeks.some(week => week.total > 0)) {
    return (
      <EmptyState>
        Nenhum chamado do cliente entre {dateLabel(weeks[0].semana)} e {dateLabel(weeks[weeks.length - 1].semana)} (início da última semana).
      </EmptyState>
    );
  }
  const color = seriesColor(theme, 0);
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={weeks} margin={{ top: 16, right: 16, bottom: 4, left: -16 }}>
        <CartesianGrid vertical={false} stroke={ink.grid} />
        <XAxis dataKey="semana" tickFormatter={value => dateLabel(value).slice(0, 5)} interval="preserveStartEnd"
          minTickGap={24} {...axisProps(theme)} />
        <YAxis allowDecimals={false} {...axisProps(theme)} />
        <ChartTooltip cursor={{ stroke: ink.axis }} content={({ active, payload }) => (active && payload?.length ? (
          <ChartTooltipBox title={`Semana de ${dateLabel(payload[0].payload.semana)}`} rows={[
            { label: "Chamados", value: payload[0].payload.total, color }
          ]} />
        ) : null)} />
        {tickets.entrega_semana && (
          <ReferenceLine x={tickets.entrega_semana} stroke={ink.secondary}
            label={{ value: "Entrega", position: "top", fill: ink.secondary, fontSize: 11 }} />
        )}
        <Line type="linear" dataKey="total" name="Chamados" stroke={color} strokeWidth={2}
          dot={weeks.length <= 16 ? { r: 4, strokeWidth: 2, stroke: theme.palette.background.paper, fill: color } : false}
          activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
};

const ProjectTab = ({ projects, projectId, onSelectProject, reloadToken }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const selected = useMemo(() => projects.find(project => project.id === projectId) || null, [projects, projectId]);

  useEffect(() => {
    setDetail(null);
    setError("");
    if (!projectId) return undefined;
    let active = true;
    setLoading(true);
    api.get(`/dashboard/implantation/projects/${projectId}`, { params: reloadToken ? { refresh: 1 } : {} })
      .then(({ data }) => { if (active) setDetail(data); })
      .catch(err => { if (active) setError(err?.response?.data?.error || "Não foi possível carregar o projeto."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId, reloadToken]);

  const project = detail?.project?.id === projectId ? detail.project : null;

  return (
    <div className={classes.grid}>
      <Autocomplete
        className={classes.selector}
        options={projects}
        value={selected}
        onChange={(_, value) => onSelectProject(value?.id || null)}
        getOptionLabel={option => `#${option.id} · ${option.cliente} · ${option.tipo || "Sem tipo"}`}
        getOptionSelected={(option, value) => option.id === value.id}
        noOptionsText="Nenhum projeto com os filtros atuais"
        renderInput={params => <TextField {...params} label="Projeto" variant="outlined" size="small" />}
      />

      {!projectId ? <Alert severity="info">Selecione um projeto para ver cronograma, horas e chamados.</Alert> :
        loading ? <div role="status" aria-label="Carregando projeto"><Skeleton variant="rect" height={96} /><br /><Skeleton variant="rect" height={320} /></div> :
        error ? <Alert severity="error">{error}</Alert> : project ? (
          <>
            <div className={classes.cards}>
              <StatCard label="Status" value={project.status} detail={`${project.tipo || "Sem tipo"} · ${project.motivo || "Sem motivo"}`} />
              <StatCard label="Previsão de término" value={dateLabel(project.previsao_termino)} detail={`Aberto em ${dateLabel(project.aberta_em)}`} accent={theme.palette.text.secondary} />
              <StatCard label="Entrega (12 Finalização)" value={dateLabel(project.entrega_em)}
                detail={project.entrega_em ? (project.entrega_aproximada ? "Data aproximada (última alteração)" : "Conclusão da etapa 12") : "Ainda não entregue"}
                accent={theme.palette.text.secondary} />
              <StatCard label="Etapas atrasadas" value={project.etapas_atrasadas} detail={project.tecnico ? `Responsável: ${project.tecnico}` : undefined}
                accent={project.etapas_atrasadas ? STATUS_COLORS.critical : theme.palette.text.secondary} />
            </div>

            <div className={classes.twoColumns}>
              <Panel title="Avanço" subtitle="Etapas concluídas sobre o total (sem canceladas e mudanças de escopo).">
                <div className={classes.progressTrack} role="progressbar" aria-valuenow={project.percentual} aria-valuemin={0} aria-valuemax={100}>
                  <div className={classes.progressFill} style={{ width: `${project.percentual}%`, background: seriesColor(theme, 0) }} />
                </div>
                <div className={classes.progressMeta}>
                  <span>{project.etapas_concluidas} de {project.etapas_total} etapas</span>
                  <strong>{project.percentual}%</strong>
                </div>
              </Panel>
              <Panel title="Horas gastas × estimadas" subtitle="Filhas dos grupos 3 (etapas) e 2 (customizações).">
                <HoursComparison hours={detail.hours} />
              </Panel>
            </div>

            <Panel title="Cronograma previsto × real"
              subtitle="Previsto: da previsão de início à de término. Real: do primeiro registro de tempo em execução até a conclusão (ou hoje). Passe o mouse em uma etapa para ver o desvio.">
              <Gantt stages={detail.stages} generatedAt={detail.generated_at} />
            </Panel>

            <div className={classes.twoColumns}>
              <Panel title="Horas por etapa" subtitle="Customizações (grupo 2) aparecem como 07 Customização.">
                <HoursByStage rows={detail.hours_by_stage} />
              </Panel>
              <Panel title="Chamados do cliente por semana" subtitle="OS do grupo Ticket N1 do mesmo cliente, da abertura do projeto até 60 dias após a entrega.">
                <TicketsByWeek tickets={detail.tickets_by_week} />
              </Panel>
            </div>

            <div className={classes.twoColumns}>
              <Panel title="Mudanças de escopo">
                <DataTable rows={detail.scope_changes} rowKey={row => row.os_id} emptyText="Nenhuma mudança de escopo."
                  columns={[
                    { key: "os_id", label: "OS", render: row => <OsLink id={row.os_id} /> },
                    { key: "descricao", label: "Descrição", render: row => row.descricao || row.rotulo },
                    { key: "status", label: "Status" },
                    { key: "aberta_em", label: "Abertura", render: row => dateLabel(row.aberta_em) },
                    { key: "minutos", label: "Horas", numeric: true, render: row => hoursLabel(row.minutos) }
                  ]} />
              </Panel>
              <Panel title="Customizações (grupo 2)">
                <DataTable rows={detail.customizations} rowKey={row => row.os_id} emptyText="Nenhuma customização vinculada."
                  columns={[
                    { key: "os_id", label: "OS", render: row => <OsLink id={row.os_id} /> },
                    { key: "descricao", label: "Descrição", render: row => row.descricao || row.rotulo },
                    { key: "status", label: "Status" },
                    { key: "tecnico", label: "Técnico", render: row => row.tecnico || "-" },
                    { key: "minutos", label: "Horas", numeric: true, render: row => hoursLabel(row.minutos) }
                  ]} />
              </Panel>
            </div>
          </>
        ) : null}
    </div>
  );
};

export default ProjectTab;
